import React, { useRef, useCallback } from 'react';
import { Crosshair } from 'lucide-react';

/**
 * FocalPointPicker — Click on an image to set the crop focus area.
 * Stores as {x: 0-100, y: 0-100} percentages.
 * When the image is displayed with object-cover, CSS object-position uses these values.
 */
export default function FocalPointPicker({ src, focalX = 50, focalY = 50, onChange, className = '' }) {
  const containerRef = useRef(null);

  const handleClick = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    onChange({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  }, [onChange]);

  if (!src) return null;

  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        <Crosshair className="inline h-3 w-3 mr-1" />
        Focal Point <span className="text-gray-400 font-normal">— click to set crop focus</span>
      </label>
      <div
        ref={containerRef}
        onClick={handleClick}
        className="relative cursor-crosshair rounded-lg overflow-hidden border-2 border-dashed border-gray-300 hover:border-[#C9A967] transition-colors"
        style={{ maxWidth: '320px' }}
        data-testid="focal-point-picker"
      >
        <img
          src={src}
          alt="Set focal point"
          className="w-full h-auto block"
          style={{ maxHeight: '220px', objectFit: 'contain' }}
          draggable="false"
        />
        {/* Focal point indicator */}
        <div
          className="absolute pointer-events-none"
          style={{ left: `${focalX}%`, top: `${focalY}%`, transform: 'translate(-50%, -50%)' }}
        >
          {/* Outer ring */}
          <div className="w-7 h-7 rounded-full border-2 border-white shadow-lg flex items-center justify-center"
            style={{ background: 'rgba(201, 169, 103, 0.35)' }}>
            {/* Inner dot */}
            <div className="w-2.5 h-2.5 rounded-full bg-[#C9A967] border border-white shadow" />
          </div>
        </div>
        {/* Crosshair lines */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bg-white/40" style={{ left: `${focalX}%`, top: 0, width: '1px', height: '100%' }} />
          <div className="absolute bg-white/40" style={{ top: `${focalY}%`, left: 0, width: '100%', height: '1px' }} />
        </div>
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-gray-400">
          Position: {focalX}% x {focalY}%
        </span>
        {(focalX !== 50 || focalY !== 50) && (
          <button
            type="button"
            onClick={() => onChange({ x: 50, y: 50 })}
            className="text-[10px] text-gray-400 hover:text-gray-600 underline"
          >
            Reset to center
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Helper: Convert focal point to CSS object-position string.
 * Returns "50% 50%" if no focal point is set.
 */
export function focalToObjectPosition(focalX, focalY) {
  const x = focalX ?? 50;
  const y = focalY ?? 50;
  if (x === 50 && y === 50) return undefined; // let CSS default handle it
  return `${x}% ${y}%`;
}
